import apiClient from './client';

// NOTE: `baseURL` override mirrors the other v2 modules (criteria/tenders) until
// apiClient is refactored to a `/api` base with versioned paths.
const V2_BASE = '/api/v2';
const ROOT = '/skills-taxonomy';

// ---------------------------------------------------------------------------
// Types — snake_case, matching the response shape after the client's
// camelCase→snake_case conversion (see api/client.ts).
// ---------------------------------------------------------------------------

export type NodeKind = 'CONCRETE' | 'CATEGORY';
export type TaxonomyStatus = 'ACTIVE' | 'DEPRECATED';
export type UnresolvedKind = 'ORPHAN' | 'DOMAINLESS_ORPHAN';
export type SuggestionReason = 'SENSE' | 'LEXICAL';

export interface NodeRef {
  node_id: string;
  label: string;
}

export interface SkillEdge {
  id: string;
  from_node: string;
  to_node: string;
  soft: boolean;
  status: TaxonomyStatus;
}

export interface SuggestedNode {
  node_id: string;
  label: string;
  kind: NodeKind;
  domain_id: string | null;
  reason: SuggestionReason;
}

export interface SkillNode {
  id: string;
  canonical_label: string;
  kind: NodeKind;
  domain_id: string | null;
  status: TaxonomyStatus;
  created_at: string;
  updated_at: string;
}

export interface SkillAlias {
  id: string;
  surface_form: string;
  node_id: string;
  status: TaxonomyStatus;
  assigned_by: string | null;
  created_at: string;
}

/** An unresolved-observation group, keyed by normalized surface. */
export interface UnresolvedItem {
  surface: string;
  kind: UnresolvedKind;
  observation_count: number;
  first_seen: string;
  last_seen: string;
  source_types: string[];
  /** A few sample verbatim source texts the surface was read from (for the hover). */
  original_texts: string[];
}

/** A same-surface mixed-state alias (lint item). */
export interface DomainlessAliasItem {
  alias_id: string;
  surface_form: string;
  node_id: string;
  node_label: string | null;
  sibling_domains: NodeRef[];
  sibling_node_labels: string[];
}

/** A node with no is_a edge and no domain (lint item). */
export interface ParentlessNodeItem {
  node_id: string;
  label: string;
}

/**
 * An active alias stranded on a deprecated node (lint item, skills_library_spec.md
 * §3.6). Its observations read unresolved for matching yet never enter the orphan
 * queue. Fix: re-point the alias, deprecate it, or reactivate the node.
 */
export interface ActiveAliasOnDeprecatedNodeItem {
  alias_id: string;
  surface_form: string;
  node_id: string;
  node_label: string | null;
}

export interface CurationQueue {
  orphans: UnresolvedItem[];
  domainless_orphans: UnresolvedItem[];
  domainless_aliases: DomainlessAliasItem[];
  parentless_nodes: ParentlessNodeItem[];
  active_aliases_on_deprecated_nodes: ActiveAliasOnDeprecatedNodeItem[];
}

/** Outcome of a surface-level orphan resolution. */
export interface OrphanResolution {
  node_id: string;
  alias_id: string;
  cleared: number;
}

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/**
 * The curation worklist. `version` is the curation switcher (versioning spec §6):
 * it scopes the orphan queue to that extraction-prompt version's unresolved
 * observations, so a curator can prepare a candidate (e.g. `v4`) queue while
 * production still serves the live version. Omitted → the production version.
 */
export async function fetchQueue(version?: string): Promise<CurationQueue> {
  const { data } = await apiClient.get<CurationQueue>(`${ROOT}/queue`, {
    baseURL: V2_BASE,
    params: version ? { version } : undefined,
  });
  return data;
}

export async function fetchNodes(): Promise<SkillNode[]> {
  const { data } = await apiClient.get<SkillNode[]>(`${ROOT}/nodes`, { baseURL: V2_BASE });
  return data;
}

export async function fetchAliases(): Promise<SkillAlias[]> {
  const { data } = await apiClient.get<SkillAlias[]>(`${ROOT}/aliases`, { baseURL: V2_BASE });
  return data;
}

export async function fetchEdges(): Promise<SkillEdge[]> {
  const { data } = await apiClient.get<SkillEdge[]>(`${ROOT}/edges`, { baseURL: V2_BASE });
  return data;
}

export async function fetchSuggestions(surface: string): Promise<SuggestedNode[]> {
  const { data } = await apiClient.get<SuggestedNode[]>(`${ROOT}/suggestions`, {
    baseURL: V2_BASE,
    params: { surface },
  });
  return data;
}

// ---------------------------------------------------------------------------
// Orphan resolution (surface-level)
// ---------------------------------------------------------------------------

export async function linkSurface(surface: string, nodeId: string): Promise<OrphanResolution> {
  const { data } = await apiClient.post<OrphanResolution>(
    `${ROOT}/orphans/link`,
    { surface, node_id: nodeId },
    { baseURL: V2_BASE },
  );
  return data;
}

export async function mintConcreteForSurface(surface: string, label: string): Promise<OrphanResolution> {
  const { data } = await apiClient.post<OrphanResolution>(
    `${ROOT}/orphans/mint-concrete`,
    { surface, label },
    { baseURL: V2_BASE },
  );
  return data;
}

export async function mintSenseForSurface(surface: string, label: string): Promise<OrphanResolution> {
  const { data } = await apiClient.post<OrphanResolution>(
    `${ROOT}/orphans/mint-sense`,
    { surface, label },
    { baseURL: V2_BASE },
  );
  return data;
}

export async function resyncOrphans(): Promise<{ cleared: number }> {
  const { data } = await apiClient.post<{ cleared: number }>(
    `${ROOT}/orphans/resync`,
    {},
    { baseURL: V2_BASE },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Candidate cohort re-extraction (versioning spec §7)
// ---------------------------------------------------------------------------

export interface ReextractionStatus {
  production_version: string;
  candidate_version: string;
  running: boolean;
}

/** Production/candidate versions and whether a full re-extraction is in progress. */
export async function fetchReextractionStatus(): Promise<ReextractionStatus> {
  const { data } = await apiClient.get<ReextractionStatus>(`${ROOT}/reextract`, { baseURL: V2_BASE });
  return data;
}

/**
 * Trigger a full corpus re-extraction into the candidate cohort (every criterion +
 * CV), leaving production untouched. Async — returns the status (202), or a 409 if a
 * run is already in progress (surfaced as a rejected promise the caller can catch).
 */
export async function triggerReextraction(): Promise<ReextractionStatus> {
  const { data } = await apiClient.post<ReextractionStatus>(`${ROOT}/reextract`, {}, { baseURL: V2_BASE });
  return data;
}

/**
 * Durable orphan rejection (storage §4.6): mark a surface as not-vocabulary-to-track.
 * It leaves the queue and future orphans of it are auto-suppressed ("forgotten").
 */
export async function rejectSurface(surface: string): Promise<void> {
  await apiClient.post(`${ROOT}/orphans/reject`, { surface }, { baseURL: V2_BASE });
}

/** Reinstate a previously-rejected surface — it returns to the queue. */
export async function reinstateSurface(surface: string): Promise<void> {
  await apiClient.delete(`${ROOT}/orphans/reject`, { baseURL: V2_BASE, params: { surface } });
}

// ---------------------------------------------------------------------------
// Alias operations
// ---------------------------------------------------------------------------

export async function repointAlias(aliasId: string, nodeId: string): Promise<SkillAlias> {
  const { data } = await apiClient.patch<SkillAlias>(
    `${ROOT}/aliases/${aliasId}/node`,
    { node_id: nodeId },
    { baseURL: V2_BASE },
  );
  return data;
}

export async function splitAlias(aliasId: string, label: string): Promise<SkillAlias> {
  const { data } = await apiClient.post<SkillAlias>(
    `${ROOT}/aliases/${aliasId}/split`,
    { label },
    { baseURL: V2_BASE },
  );
  return data;
}

export async function setAliasActive(aliasId: string, active: boolean): Promise<SkillAlias> {
  const { data } = await apiClient.patch<SkillAlias>(
    `${ROOT}/aliases/${aliasId}/status`,
    { active },
    { baseURL: V2_BASE },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Node operations
// ---------------------------------------------------------------------------

export async function relabelNode(nodeId: string, label: string): Promise<SkillNode> {
  const { data } = await apiClient.patch<SkillNode>(
    `${ROOT}/nodes/${nodeId}/label`,
    { label },
    { baseURL: V2_BASE },
  );
  return data;
}

/**
 * Deactivate / reactivate a whole node — the Skills Library toggle
 * (skills_library_spec.md §4.3). Flips `skill_node.status`.
 */
export async function setNodeActive(nodeId: string, active: boolean): Promise<SkillNode> {
  const { data } = await apiClient.patch<SkillNode>(
    `${ROOT}/nodes/${nodeId}/status`,
    { active },
    { baseURL: V2_BASE },
  );
  return data;
}

export async function setNodeDomain(nodeId: string, domainNodeId: string | null): Promise<SkillNode> {
  const { data } = await apiClient.patch<SkillNode>(
    `${ROOT}/nodes/${nodeId}/domain`,
    { domain_node_id: domainNodeId },
    { baseURL: V2_BASE },
  );
  return data;
}

export async function addParentEdge(childId: string, parentId: string, soft = false): Promise<unknown> {
  const { data } = await apiClient.post(
    `${ROOT}/nodes/${childId}/parents`,
    { parent_id: parentId, soft },
    { baseURL: V2_BASE },
  );
  return data;
}

export async function removeParentEdge(childId: string, parentId: string): Promise<void> {
  await apiClient.delete(`${ROOT}/nodes/${childId}/parents/${parentId}`, { baseURL: V2_BASE });
}
